import { SystemEntity } from "./systemEntities";

/**
 * Subset of SystemEntity values that can own charges.
 *
 * Based on the charges design document, charges can be attached to:
 * - AP Invoices (for vendor charges like freight, duty)
 * - Purchase Orders (for inbound costs)
 * - Sales Orders (for customer billings and internal costs)
 * - Shipments (for load-level freight and logistics costs)
 *
 * Charges can be attached at both header and line-item levels.
 */
export const CHARGEABLE_ENTITIES = [
  SystemEntity.AP_INVOICE,
  SystemEntity.AP_INVOICE_LINE_ITEM,
  SystemEntity.INTERNAL_PURCHASE_ORDER,
  SystemEntity.INTERNAL_PURCHASE_ORDER_LINE_ITEM,
  SystemEntity.AR_SALES_ORDER,
  SystemEntity.AR_SALES_ORDER_LINE_ITEM,
  SystemEntity.IM_TRANSFER_ORDER,
  SystemEntity.IM_TRANSFER_ORDER_LINE_ITEM,
  SystemEntity.SCM_SHIPMENT,
] as const;

export const HEADER_TO_LINE_ENTITY_MAP = {
  [SystemEntity.AP_INVOICE]: SystemEntity.AP_INVOICE_LINE_ITEM,
  [SystemEntity.INTERNAL_PURCHASE_ORDER]:
    SystemEntity.INTERNAL_PURCHASE_ORDER_LINE_ITEM,
  [SystemEntity.AR_SALES_ORDER]: SystemEntity.AR_SALES_ORDER_LINE_ITEM,
  [SystemEntity.IM_TRANSFER_ORDER]: SystemEntity.IM_TRANSFER_ORDER_LINE_ITEM,
} as const;

/**
 * Header-only entities that can own charges but don't have line-item counterparts.
 * These entities only support header-level charges.
 */
export const HEADER_ONLY_CHARGEABLE_ENTITIES = [
  SystemEntity.SCM_SHIPMENT,
] as const;

export type ChargeableEntity = (typeof CHARGEABLE_ENTITIES)[number];
export type HeaderLevelChargeableEntity =
  keyof typeof HEADER_TO_LINE_ENTITY_MAP;
export type LineLevelChargeableEntity =
  (typeof HEADER_TO_LINE_ENTITY_MAP)[HeaderLevelChargeableEntity];
export type HeaderOnlyChargeableEntity =
  (typeof HEADER_ONLY_CHARGEABLE_ENTITIES)[number];

/**
 * Type guard to check if a SystemEntity is chargeable
 */
export function isChargeableEntity(
  entity: SystemEntity,
): entity is ChargeableEntity {
  return CHARGEABLE_ENTITIES.includes(entity as ChargeableEntity);
}

export function isHeaderLevelChargeableEntity(
  entity: SystemEntity,
): entity is HeaderLevelChargeableEntity {
  return Object.keys(HEADER_TO_LINE_ENTITY_MAP).includes(entity);
}

export function isHeaderOnlyChargeableEntity(
  entity: SystemEntity,
): entity is HeaderOnlyChargeableEntity {
  return HEADER_ONLY_CHARGEABLE_ENTITIES.includes(
    entity as HeaderOnlyChargeableEntity,
  );
}

export function getHeaderEntityType(
  entity: ChargeableEntity,
): HeaderLevelChargeableEntity {
  if (isHeaderLevelChargeableEntity(entity)) {
    return entity;
  }

  const matchingEntry = Object.entries(HEADER_TO_LINE_ENTITY_MAP).find(
    ([, lineEntityType]) => lineEntityType === entity,
  );
  const headerEntityType = matchingEntry?.[0] as
    HeaderLevelChargeableEntity | undefined;

  if (!headerEntityType) {
    throw new Error(`Entity ${entity} is not a line level chargeable entity`);
  }

  return headerEntityType;
}
